/**
 * This is the Compliance Console component that allows FashionNova admin to manage
 * users accounts
 */
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, HideText, Icon } from '@fashionnovateam/nova-ui-toolkit';
import queryString from 'qs';
import toast from '../Toast/Toast';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import historyProps from '../../Lib/Utils/PropTypes/history-props';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import { complianceVendorsReport } from '../../Lib/Utils/Table/table-export';
import LoadingText from '../ReusableComponents/LoadingText/LoadingText';

import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import ComplianceDataGrid from './ComplianceDataGrid.tsx';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';

/**
 * The Compliance console component
 */
export default function ComplianceConsole(props) {
  const [params, setParams] = useState({
    page: 1,
    sortBy: 'last_logged_in_at',
    sortOrder: 'DESC',
    search: '',
    ...decodeLocation(props.location.search),
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await complianceVendorsReport();
      toast(
        {
          title: 'Success',
          text: 'Report has been downloaded',
        },
        { type: 'success', autoClose: 1000 },
      );
    } catch (error) {
      toast(
        {
          title: 'Error',
          text: error.message || 'Failed to generate report',
        },
        { type: 'error', autoClose: 1000 },
      );
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const updateParams = useCallback(
    (updates) => {
      const updateKeys = Object.keys(updates);
      const fullUpdates = { ...updates };
      if (updateKeys.includes('search')) {
        fullUpdates.page = 1;
      }

      setParams((prevParams) => {
        const newParams = {
          ...prevParams,
          ...fullUpdates,
        };
        props.history.push({
          pathname: props.location.pathname,
          search: queryString.stringify(newParams),
        });
        return newParams;
      });
    },
    [props.history.push, props.location.pathname],
  );

  // Fetch users on mount and when the params change
  useEffect(() => {
    props.fetchUsers(queryString.stringify(params));
  }, [params]);

  const hasPermissionToExport = useMemo(
    () =>
      hasPermissionTo(
        'compliance-console',
        props.currentUser.role,
        Actions.READ,
        'EXPORT',
      ),
    [props.currentUser.role],
  );

  return (
    <Page.Container>
      <Header.Major icon="building/building-apartment-two" title="Compliance" />
      <Header.Minor
        search={{
          value: params.search,
          onChange: debounce((value) => updateParams({ search: value }), 500),
        }}
      >
        {hasPermissionToExport && (
          <Button
            onClick={handleExport}
            color="secondary"
            size="sm"
            disabled={isExporting}
          >
            {isExporting ? (
              <LoadingText text="Exporting" />
            ) : (
              <>
                <Icon name="download/download-down" />
                <HideText notAbove="lg">Export</HideText>
              </>
            )}
          </Button>
        )}
      </Header.Minor>
      <Page.Content>
        <ComplianceDataGrid
          loading={props.loading}
          updateParams={updateParams}
          count={props.count}
          rows={props.users}
          params={params}
          currentUser={props.currentUser}
        />
      </Page.Content>
    </Page.Container>
  );
}

ComplianceConsole.propTypes = {
  loading: PropTypes.bool.isRequired,
  location: locationProps.isRequired,
  logout: PropTypes.func.isRequired,
  history: historyProps.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      email: PropTypes.string.isRequired,
      last_logged_in_at: PropTypes.string,
      vendor_name: PropTypes.string,
    }),
  ).isRequired,
  count: PropTypes.number.isRequired,
  fetchUsers: PropTypes.func.isRequired,
};
